// The paper key a Recovery Kit QR hands to /authorize — read ONCE from the
// URL at boot, kept in memory only, and scrubbed from the address bar as soon
// as the app has rendered. Never storage, never logs, never analytics.
//
// Two URL forms reach this page:
//
//   #paper_key=<24 words>          Kits printed from 2026-09-21 on. A fragment
//                                  never leaves the browser: it is not in the
//                                  request line, so not in any edge access log,
//                                  and never in a Referer.
//
//   ?paper_key=<24 words>&userId=… Kits printed before that. The query DOES
//     &email=…&createdAt=…         reach the edge — and its access log — before
//                                  any code here runs; nothing client-side can
//                                  prevent that. This form is honoured only so
//                                  an already-printed kit keeps working, and it
//                                  is why the words go into the fragment now.
//
// Either way the value is removed from the URL with history.replaceState, so
// it does not sit in the address bar, in synced browser history, or in
// anything that later reads window.location. The pattern is the one the UTM
// capture already uses (components/ava/attribution.ts).
//
// READING and SCRUBBING are two steps, on purpose, because of when Gatsby
// lets each happen:
//
//   stash — gatsby-browser onClientEntry, the first app code to run. From
//           then on the key is in memory and no page code needs the URL.
//   scrub — gatsby-browser onRouteUpdate, i.e. once the initial route has
//           rendered. NOT earlier: Gatsby's router snapshots window.location
//           when its module loads (before onClientEntry) and keys the page's
//           resources by pathname + query. Rewrite the URL between that
//           snapshot and the first render and the two keys disagree — the
//           dev runtime throws "EnsureResources was not able to find
//           resources" and the page never mounts (found by the e2e login,
//           which uses the query form). After the first render the router
//           keeps its snapshot and only the address bar changes.
//
// Dependency-free like accountFlowParams: gatsby-browser calls it before the
// first render, and a test imports it without the page's graph.

export type PaperKeySource = 'fragment' | 'legacy-query'

export interface PaperKeyHandoff {
  paperKey: string
  source: PaperKeySource
  /** The legacy query carried the companions a PRINTED kit has (userId and
   *  email). The e2e harness passes paper_key alone, so this distinguishes a
   *  scanned pre-2026-09-21 kit — which deserves an updated one — from a
   *  test login. */
  printedKit: boolean
}

const KEY = 'paper_key'
/** PII the legacy QR printed alongside the words; scrubbed with them. */
const LEGACY_COMPANIONS = ['userId', 'email', 'createdAt']

function paramsOf(s: string): URLSearchParams {
  return new URLSearchParams(s.replace(/^[?#]/, ''))
}

/** Pure: find the paper key in a search + hash pair. Fragment wins. */
export function parsePaperKeyHandoff(search: string | null | undefined, hash: string | null | undefined): PaperKeyHandoff | undefined {
  const h = paramsOf(hash ?? '')
  const fromHash = h.get(KEY)
  if (fromHash && fromHash.trim()) {
    return { paperKey: fromHash.trim(), source: 'fragment', printedKit: false }
  }
  const q = paramsOf(search ?? '')
  const fromQuery = q.get(KEY)
  if (fromQuery && fromQuery.trim()) {
    return {
      paperKey: fromQuery.trim(),
      source: 'legacy-query',
      printedKit: q.has('userId') || q.has('email'),
    }
  }
  return undefined
}

/**
 * Pure: the URL to put in the address bar once the key has been read, or null
 * when there is nothing to scrub. Every other query parameter and any
 * unrelated fragment survive; only the key and the legacy PII companions go.
 */
export function scrubPaperKeyHref(pathname: string, search: string, hash: string): string | null {
  // Only a part that actually carried the key is rewritten; the other part
  // is kept verbatim (re-serialising `#section` through URLSearchParams would
  // turn it into `#section=`).
  let newSearch = search
  let newHash = hash
  let touched = false
  const q = paramsOf(search)
  if (q.has(KEY)) {
    q.delete(KEY)
    for (const p of LEGACY_COMPANIONS) q.delete(p)
    const qs = q.toString()
    newSearch = qs ? '?' + qs : ''
    touched = true
  }
  const h = paramsOf(hash)
  if (h.has(KEY)) {
    h.delete(KEY)
    const hs = h.toString()
    newHash = hs ? '#' + hs : ''
    touched = true
  }
  if (!touched) return null
  return pathname + newSearch + newHash
}

let pending: PaperKeyHandoff | undefined

/**
 * Read the key off the live URL into memory. Leaves the URL alone, so it is
 * safe at any point of the boot — including before Gatsby has loaded the
 * page (see the header). Idempotent: a call that finds nothing in the URL
 * leaves the stash as it is. SSR-safe.
 */
export function stashPaperKeyFromUrl(): void {
  if (typeof window === 'undefined') return
  const { search, hash } = window.location
  const found = parsePaperKeyHandoff(search, hash)
  if (found) pending = found
}

/**
 * Remove the key (and the legacy PII companions) from the address bar. Only
 * once the initial route has rendered (see the header). SSR-safe; a history
 * API that refuses is not worth failing the page over.
 */
export function scrubPaperKeyFromUrl(): void {
  if (typeof window === 'undefined') return
  const { pathname, search, hash } = window.location
  const scrubbed = scrubPaperKeyHref(pathname, search, hash)
  if (scrubbed === null) return
  try {
    window.history.replaceState(window.history.state, '', scrubbed)
  } catch {
    /* see above */
  }
}

/** Stash, then scrub — for the hooks that run after the initial render. */
export function capturePaperKeyFromUrl(): void {
  stashPaperKeyFromUrl()
  scrubPaperKeyFromUrl()
}

/** The stashed key, if any. Does not clear it (React may render twice). */
export function peekPaperKeyHandoff(): PaperKeyHandoff | undefined {
  return pending
}

/** Forget the key once the flow holds it. */
export function clearPaperKeyHandoff(): void {
  pending = undefined
}
